import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import { Today as TodayIcon } from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors } from '../../theme/adminTheme';
import { formatSlotTime } from './WeeklySchedule';

// "2026-08-19" in the browser's own local date — same "no per-school-
// timezone translation" convention WeeklySchedule.js's day-of-week grouping
// already uses (both read GroupTimeSlot's raw day/time fields at face
// value); a school's bell schedule isn't expected to cross a date boundary
// relative to the admin viewing it.
function todayIso() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// A single resolved class meeting — period/block label (when the school's
// template links this slot to one — see Period's docstring in
// app/models.py) · bell time · class name/subject · teacher(s) · room. Every
// piece past the time is optional and simply omitted when absent, so a
// school with no template (period_label/subject/room all null) renders a
// plain "time · class name" line — no empty scaffolding.
function TodayClassRow({ cls }) {
  const teacherNames = (cls.teachers || []).map((t) => t.username).join(', ');
  const metaParts = [cls.room && `Room ${cls.room}`, teacherNames].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75, '&:not(:last-of-type)': { borderBottom: '1px solid rgba(24,24,27,0.06)' } }}>
      {cls.period_label && (
        <Chip
          label={cls.period_label}
          size="small"
          sx={{ height: 22, fontWeight: 700, fontSize: '0.7rem', bgcolor: adminColors.accentLight, color: adminColors.accentDark, flexShrink: 0 }}
        />
      )}
      <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink, minWidth: 118, flexShrink: 0 }}>
        {formatSlotTime(cls.start_time)}–{formatSlotTime(cls.end_time)}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }} noWrap>
          {cls.group_name}
          {cls.subject ? ` · ${cls.subject}` : ''}
        </Typography>
        {metaParts.length > 0 && (
          <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block' }} noWrap>
            {metaParts.join(' · ')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// "Today" panel — GET /api/users/{id}/schedule?date= resolved through the
// student's school's SchoolScheduleTemplate (period labels, bell times,
// A/B-rotation-aware) when one exists, or plain weekday GroupTimeSlot
// matching when it doesn't (Phase 4, admin console v2). Sits above the
// existing WeeklySchedule section in StudentDrawer, which is kept
// unchanged for week-at-a-glance context — this is the "richer day view"
// the phase spec asks for, not a replacement for the week view.
export default function TodaySchedule({ userId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/users/${userId}/schedule?date=${todayIso()}`,
          {},
          { signal: controller.signal }
        );
        const body = await handleApiResponse(res, navigate);
        if (body === null || controller.signal.aborted) return; // 401 — already redirected
        setData(body);
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setError(describeAdminError(err, "Couldn't load today's schedule."));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [userId, navigate]);

  const dayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <TodayIcon sx={{ fontSize: 18, color: adminColors.inkSoft }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>
          Today
        </Typography>
        <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
          {dayLabel}
        </Typography>
        {data?.template_kind === 'ab_rotation' && data?.rotation_day && (
          <Chip
            label={`Day ${data.rotation_day}`}
            size="small"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: adminColors.surface, color: adminColors.inkSoft }}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : !data || data.classes.length === 0 ? (
        <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
          No classes scheduled today.
        </Typography>
      ) : (
        <Box>
          {data.classes.map((cls, idx) => (
            <TodayClassRow
              // eslint-disable-next-line react/no-array-index-key -- idx is a last-resort tiebreak alongside group_id/period_id/start_time, which are unique in every real case
              key={`${cls.group_id}-${cls.period_id ?? 'x'}-${cls.start_time}-${idx}`}
              cls={cls}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
